import { Grid, Typography } from "@mui/material";
import { CURRENCY } from "../../constants/Variables";
import globalStyle from "../../globalStyle";
import {
  calculateLossAmount,
  calculateVat,
  handleOpenSnackBar,
  calculateProductFinalPrice,
  roundTo2Decimal,
} from "../../utils";
import Label from "../Label";
import TextInput from "../TextInputs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const Pricing = ({
  productPageType,
  inputValues,
  setInputValues,
  inputError,
  setInputError,
  vat,
}) => {
  const { t } = useTranslation(["products", "common"]);

  const handleChangeValues = (event) => {
    const { name, type, valueAsNumber, value } = event.target;

    const formattedValue = type === "number" ? valueAsNumber : value;

    const priceAfterMargin =
      inputValues.price + (inputValues.price * inputValues.profitMargin) / 100;

    if (name === "discount" && formattedValue > priceAfterMargin) {
      handleOpenSnackBar("error", t("validation.discountCannotExceedPrice"));
      return;
    }

    setInputValues((prev) => ({ ...prev, [name]: formattedValue }));
    setInputError((prev) => ({ ...prev, [name]: "" }));
  };

  const finalPrice = calculateProductFinalPrice(
    inputValues.price,
    inputValues.discount,
    inputValues.profitMargin
  );

  const vatAmount = calculateVat(finalPrice, vat);

  const lossAmount = calculateLossAmount(
    inputValues.price,
    inputValues.discount,
    inputValues.profitMargin
  );

  const finalPriceWithVat = roundTo2Decimal(finalPrice + vatAmount);


  return (
    <>
      <Grid item xs={12} >
        <Typography fontSize={20} fontWeight={600}>
          {t("pricingAndStocks")}
        </Typography>
        {productPageType === "edit" && (
          <Typography variant="body2" color="text.secondary">
            {t("note")}: {t("updatePricingAndQuantityInRestockSection")}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        <Label
          labelText={t("purchasePriceWithoutVat")}
          required
          htmlFor={"product-price"}
        />
        <TextInput
          id={"product-price"}
          styleProps={globalStyle.textField}
          placeholder={t("enterPurchasePriceWithoutVat")}
          type={"number"}
          name={"price"}
          value={inputValues.price}
          onChange={handleChangeValues}
          InputEndIcon={CURRENCY}
          inputError={Boolean(inputError.price)}
          helperText={t(inputError.price)}
          disabled={productPageType === "edit"}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Label
          optional
          labelText={t("profitMargin")}
          htmlFor={"product-profit-margin"}
        // required
        />
        <TextInput
          id={"product-profit-margin"}
          styleProps={globalStyle.textField}
          placeholder={t("enterProfitMargin")}
          type={"number"}
          name={"profitMargin"}
          value={inputValues.profitMargin}
          onChange={handleChangeValues}
          InputEndIcon={"%"}
          inputError={Boolean(inputError.profitMargin)}
          helperText={t(inputError.profitMargin)}
          disabled={productPageType === "edit"}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Label
          labelText={t("discount")}
          htmlFor={"product-discount"}
          optional
        />
        <TextInput
          id={"product-discount"}
          styleProps={globalStyle.textField}
          placeholder={t("enterDiscount")}
          type={"number"}
          name={"discount"}
          value={inputValues.discount}
          onChange={handleChangeValues}
          InputEndIcon={CURRENCY}
          inputError={Boolean(inputError.discount)}
          helperText={t(inputError.discount)}
          disabled={productPageType === "edit"}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <Label
          labelText={t("finalPrice")}
          htmlFor={"product-final-price"}
        />
        <TextInput
          id={"product-final-price"}
          styleProps={globalStyle.textField}
          readonly
          InputEndIcon={CURRENCY}
          value={finalPrice}
          inputError={finalPrice < 0}
          helperText={
            finalPrice < 0 ? t("validation.finalPriceCannotBeZeroOrNegative") : null
          }
          disabled={productPageType === "edit"}
        />
        {lossAmount < 0 && (
          <Typography variant="body2" color="warning.main">
            {t("validation.youAreLosing", { amount: lossAmount, currency: CURRENCY })}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        <Label
          labelText={t("vatAmount", { vat: vat })}
          htmlFor={"product-vat"}
        />
        <TextInput
          id={"product-vat"}
          styleProps={globalStyle.textField}
          readonly
          InputEndIcon={CURRENCY}
          value={vatAmount}
          inputError={vatAmount < 0}
          helperText={
            vatAmount < 0 ? t("validation.vatAmountCannotBeZeroOrNegative") : null
          }
          disabled={productPageType === "edit"}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Label
          labelText={t("finalPriceWithVat")}
          htmlFor={"product-final-price-with-vat"}
        />
        <TextInput
          id={"product-final-price-with-vat"}
          styleProps={globalStyle.textField}
          readonly
          InputEndIcon={CURRENCY}
          value={finalPriceWithVat}
          inputError={finalPriceWithVat < 0}
          helperText={
            finalPriceWithVat < 0 ? t("validation.finalPriceWithVatCannotBeZeroOrNegative") : null
          }
          disabled={productPageType === "edit"}
        />
      </Grid>

      {/* Hide product quantity in edit mode */}
      <Grid item xs={12} md={4}>
        <Label
          required
          labelText={t("quantity")}
          htmlFor={"product-quantity"}
        />
        <TextInput
          id={"product-quantity"}
          styleProps={globalStyle.textField}
          placeholder={t("enterQuantity")}
          type={"number"}
          name={"quantity"}
          value={inputValues.quantity}
          onChange={handleChangeValues}
          inputError={Boolean(inputError.quantity)}
          helperText={t(inputError.quantity)}
          disabled={productPageType === "edit"}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Label
          required
          labelText={t("sku")}
          htmlFor={"product-sku"}
        />
        <TextInput
          id={"product-sku"}
          styleProps={globalStyle.textField}
          placeholder={t("enterSku")}
          type={"text"}
          name={"sku"}
          value={inputValues.sku}
          onChange={handleChangeValues}
          inputError={Boolean(inputError.sku)}
          helperText={t(inputError.sku)}
          disabled={productPageType === "edit"}
        />
      </Grid>
    </>
  );
};

export default Pricing;
